// src/features/datev/components/DatevEmployees.tsx
//
// Mitarbeiter-Tab (Stufe C): Arbeitsoberfläche für Mitarbeiterstammdaten auf
// Basis des DATEV-Spiegels (datev_employees) — sofort da, ohne Warten auf
// DATEV-Jobs. Änderungen laufen als protokollierte Änderungsaufträge
// (datev_employee_changes) über die Edge Function datev-employee-change;
// die Bestätigung übernimmt der Hintergrund-Poller (datev-employee-sync).
//
// Abgrenzung: Der Tab "DATEV Lohnaustauschdatenservice" (DatevExchange.tsx)
// bleibt das technische Audit-Werkzeug mit Live-Jobs. Dieser Tab hier ist
// das Tagesgeschäft-Gegenstück auf Spiegel-Basis.
//
// Feldgruppen: Editierbar sind nur Gruppen in EDITABLE_FIELD_GROUPS.
// Zum Start ausschließlich 'address' (einzige empirisch verifizierte Gruppe,
// Systematik-Test 08./09.07.2026). Weitere Gruppen werden in Stufe D nach
// ihrem jeweiligen Mini-Systematik-Test hier freigeschaltet.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  RefreshCw, AlertCircle, Search, Pencil, CheckCircle,
  Clock, XCircle, ArrowLeft, Send
} from 'lucide-react';
import { supabase } from '../../../shared/services/supabase';
import {
  datevExchangeService,
  DatevExchangeEmployee
} from '../../../services/datev/DatevExchangeService';
import { EmployeeCard, DatevErrorPanel } from './EmployeeCard';
import { VERDECKT, anzeigenAusSpiegel, verdecke } from '../werteSchutz';

// ============================================
// FELDGRUPPEN-FREISCHALTUNG (Stufe D erweitert diese Liste)
// ============================================

const EDITABLE_FIELD_GROUPS: string[] = ['address'];

// Editierbare Felder der Gruppe "Adresse" — deckungsgleich mit dem
// empirisch verifizierten PUT-Vertrag (Teilupdate: nur gesendete Felder
// werden angefasst; leerer String löscht ein Feld).
const ADDRESS_FIELDS: { key: string; label: string }[] = [
  { key: 'street', label: 'Straße' },
  { key: 'house_number', label: 'Hausnummer' },
  { key: 'address_affix', label: 'Adresszusatz' },
  { key: 'postal_code', label: 'PLZ' },
  { key: 'city', label: 'Stadt' },
  { key: 'country', label: 'Land (Schlüssel)' }
];

// ============================================
// TYPEN
// ============================================

interface DatevEmployeesProps {
  customerId: string;
  beraternummer: string;
  mandantennummer: string;
  datevLohnsystem: string;
}

interface MirrorEmployee {
  id: string;
  personalnummer: string;
  nachname: string;
  vorname: string;
  eintrittsdatum: string | null;
  austrittsdatum: string | null;
  last_synced_at: string | null;
  raw_data: DatevExchangeEmployee | null;
  // Werteschutz: Masken der verdeckten Werte (12.10.2026)
  iban_maske: string | null;
  sv_nummer_maske: string | null;
  sv_nummer_eu_maske: string | null;
  steuer_id_maske: string | null;
}

interface EmployeeChange {
  id: string;
  personalnummer: string;
  reference_date: string;
  changed_fields: Record<string, { alt: any; neu: any }>;
  job_uuid: string | null;
  status: 'pending' | 'submitted' | 'confirmed' | 'failed';
  error_data: any;
  initiated_by: string;
  created_at: string;
  confirmed_at: string | null;
}

// ============================================
// HELPER
// ============================================

/** Aktueller Kalendermonat in Europe/Berlin als "YYYY-MM" (analog Edge Function) */
function currentMonthBerlin(): string {
  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit'
  }).formatToParts(new Date());
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  return `${year}-${month}`;
}

/** Zeitstempel deutsch formatieren */
function fmtTimestamp(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  }) + ' Uhr';
}

/** YYYY-MM-DD → DD.MM.YYYY */
function fmtDate(value: string | null): string {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  return String(value);
}

/** Anzeigewert für alt/neu im Änderungsprotokoll. Verschlüsselte Werte
 *  (Werteschutz, Format v1) erscheinen verdeckt. */
function fmtChangeValue(value: any): string {
  if (value === null || value === undefined || value === '') return '(leer)';
  if (typeof value === 'string' && value.startsWith('v1:')) return VERDECKT;
  return String(value);
}

/** Status eines Änderungsauftrags → Label + Farben */
function changeStatusMeta(change: EmployeeChange, lastSyncedAt: string | null): {
  label: string; color: string; bg: string; Icon: any;
} {
  if (change.status === 'failed') {
    return { label: 'Fehlgeschlagen', color: '#991B1B', bg: '#FEE2E2', Icon: XCircle };
  }
  if (change.status === 'confirmed') {
    const awaitingSync = change.confirmed_at && lastSyncedAt
      ? new Date(change.confirmed_at) > new Date(lastSyncedAt)
      : !!change.confirmed_at;
    return awaitingSync
      ? { label: 'Bestätigt — Spiegel-Sync ausstehend', color: '#92400E', bg: '#FEF3C7', Icon: CheckCircle }
      : { label: 'Bestätigt', color: '#166534', bg: '#DCFCE7', Icon: CheckCircle };
  }
  return { label: 'Eingereicht — wartet auf DATEV', color: '#1E40AF', bg: '#DBEAFE', Icon: Clock };
}

// ============================================
// STYLE-KONSTANTEN (Idiom der bestehenden DATEV-Komponenten)
// ============================================

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #D1D5DB',
  borderRadius: '6px',
  fontSize: '14px',
  backgroundColor: 'white',
  width: '100%',
  boxSizing: 'border-box'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '4px'
};

// ============================================
// HAUPT-KOMPONENTE
// ============================================

export const DatevEmployees: React.FC<DatevEmployeesProps> = ({
  customerId,
  beraternummer,
  mandantennummer,
  datevLohnsystem
}) => {
  const clientId = `${beraternummer}-${mandantennummer}`;
  // Target-System aus dem Lohnsystem des Mandanten (customers.abrechnungsmethode)
  const targetSystem: 'lug' | 'lodas' = datevLohnsystem === 'lodas' ? 'lodas' : 'lug';

  const [employees, setEmployees] = useState<MirrorEmployee[]>([]);
  const [changes, setChanges] = useState<EmployeeChange[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPnr, setSelectedPnr] = useState<string | null>(null);

  // Bearbeitung (Feldgruppe Adresse)
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressValues, setAddressValues] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<{ changeId: string; jobUuid: string } | null>(null);

  // ============================================
  // DATEN LADEN (Spiegel + Änderungsaufträge)
  // ============================================

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [empRes, changeRes] = await Promise.all([
        supabase
          .from('datev_employees')
          .select('id, personalnummer, nachname, vorname, eintrittsdatum, austrittsdatum, last_synced_at, raw_data, iban_maske, sv_nummer_maske, sv_nummer_eu_maske, steuer_id_maske')
          .eq('customer_id', customerId)
          .eq('beraternummer', beraternummer)
          .eq('mandantennummer', mandantennummer)
          .limit(100000),
        supabase
          .from('datev_employee_changes')
          .select('id, personalnummer, reference_date, changed_fields, job_uuid, status, error_data, initiated_by, created_at, confirmed_at')
          .eq('customer_id', customerId)
          .eq('client_id', clientId)
          .order('created_at', { ascending: false })
          .limit(100000)
      ]);

      if (empRes.error) throw new Error(`Spiegel-Abfrage fehlgeschlagen: ${empRes.error.message}`);
      if (changeRes.error) throw new Error(`Änderungs-Abfrage fehlgeschlagen: ${changeRes.error.message}`);

      const sorted = (empRes.data || []).slice().sort((a: MirrorEmployee, b: MirrorEmployee) => {
        const na = parseInt(a.personalnummer, 10);
        const nb = parseInt(b.personalnummer, 10);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return String(a.personalnummer).localeCompare(String(b.personalnummer));
      });

      setEmployees(sorted as MirrorEmployee[]);
      setChanges((changeRes.data || []) as EmployeeChange[]);
    } catch (err: any) {
      setLoadError(err.message || 'Unbekannter Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  }, [customerId, beraternummer, mandantennummer, clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // ABGELEITETE DATEN
  // ============================================

  const selectedEmployee = useMemo(
    () => employees.find(e => e.personalnummer === selectedPnr) || null,
    [employees, selectedPnr]
  );

  const changesByPnr = useMemo(() => {
    const map = new Map<string, EmployeeChange[]>();
    for (const c of changes) {
      const list = map.get(c.personalnummer) || [];
      list.push(c);
      map.set(c.personalnummer, list);
    }
    return map;
  }, [changes]);

  /** Badge-Info für die Listenzeile: relevantester (jüngster nicht-abgeschlossener bzw. sync-ausstehender) Auftrag */
  const listBadgeForEmployee = useCallback((emp: MirrorEmployee) => {
    const empChanges = changesByPnr.get(emp.personalnummer) || [];
    const relevant = empChanges.find(c =>
      c.status === 'pending' ||
      c.status === 'submitted' ||
      (c.status === 'confirmed' && c.confirmed_at && emp.last_synced_at &&
        new Date(c.confirmed_at) > new Date(emp.last_synced_at))
    );
    if (!relevant) return null;
    return changeStatusMeta(relevant, emp.last_synced_at);
  }, [changesByPnr]);

  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter(e =>
      e.personalnummer.toLowerCase().includes(term) ||
      (e.nachname || '').toLowerCase().includes(term) ||
      (e.vorname || '').toLowerCase().includes(term)
    );
  }, [employees, searchTerm]);

  const newestSync = useMemo(() => {
    let max: string | null = null;
    for (const e of employees) {
      if (e.last_synced_at && (!max || e.last_synced_at > max)) max = e.last_synced_at;
    }
    return max;
  }, [employees]);

  // ============================================
  // BEARBEITUNG: Adresse
  // ============================================

  const startEditAddress = () => {
    if (!selectedEmployee) return;
    const addr: any = selectedEmployee.raw_data?.address || {};
    const initial: Record<string, string> = {};
    for (const f of ADDRESS_FIELDS) {
      const v = addr[f.key];
      initial[f.key] = v === null || v === undefined ? '' : String(v);
    }
    setAddressValues(initial);
    setSubmitError(null);
    setSubmitSuccess(null);
    setIsEditingAddress(true);
  };

  /** alt→neu-Differenz gegen den Spiegel berechnen (nur wirklich geänderte Felder) */
  const computeAddressChanges = (): {
    changedFields: Record<string, { alt: any; neu: any }>;
    addressBody: Record<string, string>;
  } => {
    const addr: any = selectedEmployee?.raw_data?.address || {};
    const changedFields: Record<string, { alt: any; neu: any }> = {};
    const addressBody: Record<string, string> = {};
    for (const f of ADDRESS_FIELDS) {
      const alt = addr[f.key] === null || addr[f.key] === undefined ? '' : String(addr[f.key]);
      const neu = (addressValues[f.key] ?? '').trim();
      if (alt !== neu) {
        changedFields[`address.${f.key}`] = { alt: addr[f.key] ?? null, neu: neu };
        addressBody[f.key] = neu;
      }
    }
    return { changedFields, addressBody };
  };

  const pendingChanges = isEditingAddress ? computeAddressChanges() : { changedFields: {}, addressBody: {} };
  const pendingChangeCount = Object.keys(pendingChanges.changedFields).length;

  const submitAddressChange = async () => {
    if (!selectedEmployee || !selectedEmployee.raw_data) return;
    const { changedFields, addressBody } = computeAddressChanges();
    if (Object.keys(changedFields).length === 0) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      // LODAS-Empirie 14.07.2026 (#DXLO02418 "The field country is missing"):
      // LODAS validiert die Adress-Ressource als Ganzes und verlangt das Land
      // IM PAYLOAD — auch wenn es unverändert bleibt. Fehlt es, wird die
      // komplette Adresse verworfen (Job endet partially_successful). Deshalb
      // wird das Land jedem Adress-Auftrag beigelegt (Formularwert = Spiegel-
      // wert, falls unverändert). Die Historie (changedFields) bleibt davon
      // unberührt: Dort steht das Land nur bei echter Änderung. Für LuG ist
      // das Mitsenden des unveränderten Landes nachweislich harmlos.
      const countryValue = (addressValues['country'] ?? '').trim();
      if (!addressBody['country'] && countryValue) {
        addressBody['country'] = countryValue;
      }

      // Exakter, empirisch verifizierter PUT-Vertrag: Identifikation
      // (surname + first_name) + nur die geänderten Adressfelder.
      const response = await datevExchangeService.submitEmployeeChange(
        { customerId, clientId, targetSystem: targetSystem },
        {
          personalnummer: selectedEmployee.personalnummer,
          referenceDate: currentMonthBerlin(),
          changedFields: changedFields,
          requestBody: {
            surname: selectedEmployee.raw_data.surname,
            first_name: selectedEmployee.raw_data.first_name,
            address: addressBody
          }
        }
      );
      setSubmitSuccess({ changeId: response.change_id, jobUuid: response.job_uuid });
      setIsEditingAddress(false);
      await loadData();
    } catch (err: any) {
      setSubmitError(err.error || err.message || 'Änderungsauftrag fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Beim Wechsel des Mitarbeiters Bearbeitungszustand zurücksetzen
  useEffect(() => {
    setIsEditingAddress(false);
    setSubmitError(null);
    setSubmitSuccess(null);
  }, [selectedPnr]);

  // ============================================
  // RENDER: Fehlerdaten eines fehlgeschlagenen Auftrags
  // ============================================

  const renderChangeError = (errorData: any) => {
    if (!errorData) return null;
    // DATEV-Fehlerformat (errors-Array) strukturiert anzeigen, sonst Roh-JSON
    const errors = errorData?.errors || errorData?.response?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return <div style={{ marginTop: '8px' }}><DatevErrorPanel errors={errors} title="DATEV-Fehler" /></div>;
    }
    return (
      <pre style={{
        margin: '8px 0 0 0', padding: '10px',
        backgroundColor: '#1F2937', color: '#F9FAFB',
        borderRadius: '4px', overflowX: 'auto',
        maxHeight: '200px', overflowY: 'auto',
        fontSize: '11px', lineHeight: '1.5',
        fontFamily: 'Menlo, Monaco, "Courier New", monospace'
      }}>
        {JSON.stringify(verdecke(errorData), null, 2)}
      </pre>
    );
  };

  // ============================================
  // RENDER: Historie eines Mitarbeiters
  // ============================================

  const renderHistory = (emp: MirrorEmployee) => {
    const empChanges = changesByPnr.get(emp.personalnummer) || [];
    if (empChanges.length === 0) return null;

    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '16px 20px',
        marginBottom: '12px'
      }}>
        <h4 style={{
          fontSize: '13px', fontWeight: '600', color: '#6B7280',
          textTransform: 'uppercase', letterSpacing: '0.5px',
          margin: '0 0 12px 0', paddingBottom: '8px',
          borderBottom: '1px solid #F3F4F6'
        }}>
          Änderungshistorie ({empChanges.length})
        </h4>

        {empChanges.map(change => {
          const meta = changeStatusMeta(change, emp.last_synced_at);
          const MetaIcon = meta.Icon;
          return (
            <div key={change.id} style={{
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              padding: '12px 14px',
              marginBottom: '10px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  backgroundColor: meta.bg, color: meta.color,
                  padding: '3px 10px', borderRadius: '4px',
                  fontSize: '12px', fontWeight: '600'
                }}>
                  <MetaIcon size={13} />
                  {meta.label}
                </span>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>
                  {fmtTimestamp(change.created_at)} · {change.initiated_by}
                  {change.confirmed_at && <> · bestätigt {fmtTimestamp(change.confirmed_at)}</>}
                </span>
              </div>

              <div style={{ marginTop: '10px' }}>
                {Object.entries(change.changed_fields || {}).map(([field, diff]) => (
                  <div key={field} style={{ fontSize: '13px', color: '#111827', marginBottom: '4px' }}>
                    <span style={{ fontFamily: 'Menlo, Monaco, monospace', fontSize: '12px', color: '#6B7280' }}>
                      {field}
                    </span>
                    {': '}
                    <span style={{ color: '#991B1B' }}>{fmtChangeValue(diff?.alt)}</span>
                    {' → '}
                    <span style={{ color: '#166534', fontWeight: '500' }}>{fmtChangeValue(diff?.neu)}</span>
                  </div>
                ))}
              </div>

              {change.status === 'failed' && renderChangeError(change.error_data)}
            </div>
          );
        })}
      </div>
    );
  };

  // ============================================
  // RENDER
  // ============================================

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
        <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: '8px' }} />
        Mitarbeiter werden geladen...
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{
        backgroundColor: '#FEE2E2', border: '1px solid #FECACA',
        borderRadius: '6px', padding: '16px', fontSize: '14px', color: '#991B1B',
        display: 'flex', alignItems: 'flex-start', gap: '8px'
      }}>
        <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <strong>Fehler beim Laden:</strong> {loadError}
          <div style={{ marginTop: '8px' }}>
            <button onClick={loadData} style={{
              padding: '6px 12px', backgroundColor: '#991B1B', color: 'white',
              border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
            }}>
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // DETAIL-ANSICHT
  // ============================================

  if (selectedEmployee) {
    const raw = selectedEmployee.raw_data;
    return (
      <div>
        <button
          onClick={() => setSelectedPnr(null)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: 'none', border: 'none', color: '#6B7280',
            fontSize: '13px', cursor: 'pointer', marginBottom: '16px', padding: 0
          }}
        >
          <ArrowLeft size={14} />
          Zurück zur Liste
        </button>

        {/* Sync-Stand */}
        <div style={{
          backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB',
          borderRadius: '6px', padding: '10px 12px', marginBottom: '16px',
          fontSize: '13px', color: '#374151',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px'
        }}>
          <span>
            Spiegel-Stand dieses Datensatzes: <strong>{fmtTimestamp(selectedEmployee.last_synced_at)}</strong>
          </span>
          <span style={{ fontSize: '12px', color: '#6B7280' }}>
            Der Spiegel wird ca. alle 30 Minuten automatisch aus DATEV aktualisiert.
          </span>
        </div>

        {/* Erfolgsmeldung nach Einreichung */}
        {submitSuccess && (
          <div style={{
            backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0',
            borderRadius: '6px', padding: '12px', marginBottom: '16px',
            fontSize: '13px', color: '#166534'
          }}>
            <strong>Änderungsauftrag eingereicht.</strong> DATEV verarbeitet die Änderung asynchron
            (typisch 4–13 Minuten); die Bestätigung erfolgt automatisch und erscheint in der
            Änderungshistorie unten. Der Spiegel aktualisiert sich mit dem nächsten Lesezyklus.
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#15803D', fontFamily: 'Menlo, Monaco, monospace' }}>
              Auftrag: {submitSuccess.changeId} · Job: {submitSuccess.jobUuid}
            </div>
          </div>
        )}

        {/* Bearbeitungs-Panel: Adresse */}
        {EDITABLE_FIELD_GROUPS.includes('address') && raw && (
          <div style={{
            backgroundColor: 'white', border: '1px solid #E5E7EB',
            borderRadius: '8px', padding: '16px 20px', marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{
                fontSize: '13px', fontWeight: '600', color: '#6B7280',
                textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0
              }}>
                Adresse bearbeiten
              </h4>
              {!isEditingAddress && (
                <button
                  onClick={startEditAddress}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 12px', backgroundColor: '#2563EB', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
                  }}
                >
                  <Pencil size={13} />
                  Bearbeiten
                </button>
              )}
            </div>

            {isEditingAddress && (
              <div style={{ marginTop: '14px' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px'
                }}>
                  {ADDRESS_FIELDS.map(f => (
                    <div key={f.key}>
                      <label style={labelStyle}>{f.label}</label>
                      <input
                        type="text"
                        value={addressValues[f.key] ?? ''}
                        onChange={e => setAddressValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                        style={inputStyle}
                      />
                    </div>
                  ))}
                </div>

                {/* Vorschau der Änderungen (alt → neu) vor dem Einreichen */}
                {pendingChangeCount > 0 && (
                  <div style={{
                    marginTop: '14px', backgroundColor: '#EFF6FF',
                    border: '1px solid #DBEAFE', borderRadius: '6px',
                    padding: '10px 12px', fontSize: '13px', color: '#1E40AF'
                  }}>
                    <strong>{pendingChangeCount} Feld{pendingChangeCount > 1 ? 'er' : ''} geändert:</strong>
                    {Object.entries(pendingChanges.changedFields).map(([field, diff]) => (
                      <div key={field} style={{ marginTop: '4px' }}>
                        <span style={{ fontFamily: 'Menlo, Monaco, monospace', fontSize: '12px' }}>{field}</span>
                        {': '}
                        {fmtChangeValue(diff.alt)} → <strong>{fmtChangeValue(diff.neu)}</strong>
                      </div>
                    ))}
                  </div>
                )}

                {submitError && (
                  <div style={{
                    marginTop: '12px', backgroundColor: '#FEE2E2',
                    border: '1px solid #FECACA', borderRadius: '6px',
                    padding: '10px 12px', fontSize: '13px', color: '#991B1B',
                    display: 'flex', alignItems: 'flex-start', gap: '8px'
                  }}>
                    <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <div>{submitError}</div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                  <button
                    onClick={submitAddressChange}
                    disabled={isSubmitting || pendingChangeCount === 0}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '8px 16px',
                      backgroundColor: isSubmitting || pendingChangeCount === 0 ? '#9CA3AF' : '#10B981',
                      color: 'white', border: 'none', borderRadius: '6px',
                      cursor: isSubmitting || pendingChangeCount === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '14px', fontWeight: '500'
                    }}
                  >
                    {isSubmitting
                      ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Wird eingereicht...</>
                      : <><Send size={14} /> Änderung einreichen</>}
                  </button>
                  <button
                    onClick={() => { setIsEditingAddress(false); setSubmitError(null); }}
                    disabled={isSubmitting}
                    style={{
                      padding: '8px 16px', backgroundColor: 'white', color: '#374151',
                      border: '1px solid #D1D5DB', borderRadius: '6px',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: '14px'
                    }}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}

            {!isEditingAddress && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#6B7280' }}>
                Änderungen werden als protokollierter Auftrag an DATEV übermittelt und automatisch bestätigt.
                Weitere Feldgruppen werden nach ihrem Systematik-Test freigeschaltet.
              </div>
            )}
          </div>
        )}

        {/* Änderungshistorie */}
        {renderHistory(selectedEmployee)}

        {/* Personalakte (read-only, aus dem Spiegel) */}
        {raw ? (
          <EmployeeCard
            employee={raw}
            geschuetzt={{
              masken: {
                iban: selectedEmployee.iban_maske,
                sv_nummer: selectedEmployee.sv_nummer_maske,
                sv_nummer_eu: selectedEmployee.sv_nummer_eu_maske,
                steuer_id: selectedEmployee.steuer_id_maske
              },
              anzeigen: (feld) => anzeigenAusSpiegel(selectedEmployee.id, feld)
            }}
          />
        ) : (
          <div style={{
            backgroundColor: '#FEF3C7', border: '1px solid #FDE68A',
            borderRadius: '6px', padding: '14px', fontSize: '13px', color: '#78350F'
          }}>
            Für diesen Mitarbeiter liegt noch kein vollständiger Spiegel-Datensatz vor.
            Er wird mit dem nächsten Lesezyklus ergänzt.
          </div>
        )}
      </div>
    );
  }

  // ============================================
  // LISTEN-ANSICHT
  // ============================================

  return (
    <div>
      {/* Kopfzeile: Info + Sync-Stand + Aktionen */}
      <div style={{
        backgroundColor: '#EFF6FF', border: '1px solid #DBEAFE',
        borderRadius: '6px', padding: '12px', marginBottom: '16px',
        fontSize: '14px', color: '#1E40AF',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px'
      }}>
        <div>
          <strong>Mitarbeiter (DATEV-Spiegel):</strong> {employees.length} Mitarbeiter für Bestand <strong>{clientId}</strong>
          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#1E3A8A' }}>
            · Lohnsystem: <strong>{targetSystem.toUpperCase()}</strong>
          </span>
        </div>
        <div style={{ fontSize: '12px', color: '#1E3A8A' }}>
          Letzter Sync: <strong>{fmtTimestamp(newestSync)}</strong> · Automatische Aktualisierung ca. alle 30 Min
        </div>
      </div>

      {/* Suche + Refresh */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 320px', maxWidth: '420px' }}>
          <Search size={15} style={{ position: 'absolute', left: '10px', top: '10px', color: '#9CA3AF' }} />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Suche nach Name oder Personalnummer..."
            style={{ ...inputStyle, paddingLeft: '32px' }}
          />
        </div>
        <button
          onClick={loadData}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', backgroundColor: 'white', color: '#374151',
            border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
          }}
        >
          <RefreshCw size={14} />
          Aktualisieren
        </button>
      </div>

      {/* Tabelle */}
      {filteredEmployees.length === 0 ? (
        <div style={{
          padding: '32px', textAlign: 'center', color: '#6B7280', fontSize: '14px',
          backgroundColor: '#F9FAFB', borderRadius: '8px'
        }}>
          {employees.length === 0
            ? 'Noch keine Mitarbeiter im Spiegel — der erste Sync-Lauf befüllt ihn automatisch.'
            : 'Keine Treffer für die aktuelle Suche.'}
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: '#6B7280', fontWeight: '500', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>PNr</th>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: '#6B7280', fontWeight: '500', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: '#6B7280', fontWeight: '500', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Eintritt</th>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: '#6B7280', fontWeight: '500', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Austritt</th>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: '#6B7280', fontWeight: '500', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map(emp => {
                const badge = listBadgeForEmployee(emp);
                const BadgeIcon = badge?.Icon;
                return (
                  <tr
                    key={emp.personalnummer}
                    onClick={() => setSelectedPnr(emp.personalnummer)}
                    style={{ borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F9FAFB')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                  >
                    <td style={{ padding: '10px 14px', fontFamily: 'Menlo, Monaco, monospace', fontSize: '13px' }}>
                      {emp.personalnummer}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: '500', color: '#111827' }}>
                      {emp.nachname}, {emp.vorname}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>{fmtDate(emp.eintrittsdatum)}</td>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>{fmtDate(emp.austrittsdatum)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {badge ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          backgroundColor: badge.bg, color: badge.color,
                          padding: '2px 8px', borderRadius: '4px',
                          fontSize: '12px', fontWeight: '600'
                        }}>
                          {BadgeIcon && <BadgeIcon size={12} />}
                          {badge.label}
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#9CA3AF' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
